import React, { useState } from "react";
import { ONBOARDING_FORM } from "config/environment";

export default function OnBoarding({ userData, formData }: any) {
  const email = userData?.email || userData?.data?.user?.email;
  const formId = ONBOARDING_FORM;

  const baseUrl = `https://api.leadconnectorhq.com/widget/form/${formId}`;
  const [iframeURL, setIframeURL] = useState(baseUrl);

  React.useEffect(() => {
    if (formData && formData.length) {
      const params: string[] = [];
      let searchParams: URLSearchParams | null = null;

      if (typeof window !== "undefined") {
        searchParams = new URLSearchParams(window.location.search);
        if (email) searchParams.set("email", email);
      }

      // Mapping of form data queryKeys to actual form field data-q values
      const fieldMapping: { [key: string]: string } = {
        "business_wants": "professional_wants",
        "personal_big_win_180_days": "personal_big_win_(180_days)",
        "physical_big_win_180_days": "physical_big_win_(180_days)",
        "spiritual_big_win_180_days": "spiritual_big_win_(180_days)",
        "financial_big_win_180_days": "financial_big_win_(180_days)",
        "relational_big_win_180_days": "relational_big_win_(180_days)",
        "professional_big_win_180_days": "professional_big_win_(180_days)",
        "social_profile_name__handle": "social_profile_name_/_handle",
        "t_shirt_size": "5Zdp4nifOt6J0tJR41Pf"
      };

      // Special handling for dropdown/select fields
      const dropdownValueMapping: { [key: string]: { [key: string]: string } } = {
        "selfbelief_fulfillment": {
          "1": "1 (Unfulfilled)",
          "2": "2",
          "3": "3",
          "4": "4",
          "5": "5",
          "6": "6",
          "7": "7",
          "8": "8",
          "9": "9",
          "10": "10 (Very Fulfilled)"
        },
        "physical_fulfillment": {
          "1": "1 (Unfulfilled)",
          "2": "2",
          "3": "3",
          "4": "4",
          "5": "5",
          "6": "6",
          "7": "7",
          "8": "8",
          "9": "9",
          "10": "10 (Very Fulfilled)"
        },
        "daily_routine_fulfillment": {
          "1": "1 (Unfulfilled)",
          "2": "2",
          "3": "3",
          "4": "4",
          "5": "5",
          "6": "6",
          "7": "7",
          "8": "8",
          "9": "9",
          "10": "10 (Very Fulfilled)"
        },
        "5Zdp4nifOt6J0tJR41Pf": {
          "XS": "XS",
          "S": "S",
          "M": "M",
          "L": "L",
          "XL": "XL",
          "XXL": "XXL"
        }
      };

      // Hardcoded field keys that should always be processed (even if missing in formData)
      const mandatoryFieldKeys = [
        "selfbelief_fulfillment",
        "5Zdp4nifOt6J0tJR41Pf" // T-shirt size field
      ];

      // Create a map of existing formData for easy lookup
      const formDataMap = new Map();
      formData.forEach((item: any) => {
        if (item?.queryKey && item?.value !== null && item?.value !== undefined && item?.value !== "") {
          formDataMap.set(item.queryKey, item.value);
        }
      });

      // Process all fields - both from formData and mandatory fields
      const allFieldsToProcess = [
        ...formData,
        ...mandatoryFieldKeys
          .filter(key => !formDataMap.has(key)) // Only add if not already in formData
          .map(key => ({
            queryKey: key,
            value: formDataMap.get(key) || "" // Use existing value or empty
          }))
      ];

      allFieldsToProcess.forEach((item: any) => {
        // Skip huge internal field
        if (item?.queryKey === "signatureHash") return;

        const value = item?.value;

        // For mandatory fields, allow empty values to ensure they're processed
        if (value === null || value === undefined) return;

        // Only allow string/number (empty string is allowed for mandatory fields)
        if (typeof value !== "string" && typeof value !== "number") return;

        const rawVal = String(value).trim();

        // For non-mandatory fields, skip empty values
        if (!mandatoryFieldKeys.includes(item?.queryKey) && !rawVal) return;

        const queryKey: string | undefined = item?.queryKey;

        let finalKey = "";
        let finalValue = rawVal;

        if (queryKey) {
          // First, check if we need to map this field
          if (fieldMapping[queryKey]) {
            finalKey = fieldMapping[queryKey];
          } else {
            // Otherwise, convert __ to _/_ in queryKey
            finalKey = queryKey.replace(/__/g, "_/_");
          }

          // Handle dropdown fields - map values to option text
          if (dropdownValueMapping[queryKey] && dropdownValueMapping[queryKey][rawVal]) {
            finalValue = dropdownValueMapping[queryKey][rawVal];
          } else if (dropdownValueMapping[finalKey] && dropdownValueMapping[finalKey][rawVal]) {
            finalValue = dropdownValueMapping[finalKey][rawVal];
          }
        }

        // If no queryKey, use processed name
        if (!finalKey) {
          finalKey = item?.name
            ?.trim()
            ?.toLowerCase()
            ?.split(" ")
            ?.join("_")
            ?.replace(/__/g, "_/_") || "";
        }

        if (!finalKey) return;

        const parsedVal = finalValue.split(" ").join("+");

        // For iframe params
        params.push(`${finalKey}=${parsedVal}`);

        // For route params
        if (searchParams) {
          searchParams.set(finalKey, finalValue);
        }

        console.log(`Field: ${queryKey} -> ${finalKey} = ${finalValue}`);
      });

      // Build the final iframe URL
      let finalIframeUrl = baseUrl;
      const allParams: string[] = [];

      // Add email if available
      if (email) {
        allParams.push(`email=${encodeURIComponent(email)}`);
      }

      // Add form data params
      if (params.length) {
        allParams.push(...params);
      }

      // Construct final iframe URL
      if (allParams.length) {
        finalIframeUrl = `${baseUrl}?${allParams.join("&")}`;
      }

      // Update iframe URL state
      setIframeURL(finalIframeUrl);

      // Update browser route query params
      if (searchParams && typeof window !== "undefined") {
        const newRouteUrl = window.location.pathname + "?" + searchParams.toString();
        window.history.replaceState({}, "", newRouteUrl);

        console.log("Updated iframe URL:", finalIframeUrl);
        console.log("Updated route URL:", newRouteUrl);
      }
    }
  }, [formData, email, baseUrl]);

  return (
    <div>
      <iframe
        src={iframeURL}
        style={{
          width: "100%",
          height: "max-content",
          border: "none",
          borderRadius: "4px",
        }}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation"
        id={`inline-${formId}`}
        data-layout="{'id':'INLINE'}"
        data-trigger-type="alwaysShow"
        data-trigger-value
        data-activation-type="alwaysActivated"
        data-activation-value
        data-deactivation-type="neverDeactivate"
        data-deactivation-value
        data-form-name="On Boarding"
        data-height={542}
        data-layout-iframe-id={`inline-${formId}`}
        data-form-id={formId}
        title="On Boarding"
        loading="eager"
      ></iframe>
    </div>
  );
}